import { i18n } from 'i18n';
import {
  AddStopeNoteApproverCommand,
  FileParameter,
  FileResponse,
  PublicStopeNoteApprovalViewDto,
  RemoveStopeNoteApproverCommand,
  ResendStopeNoteApprovalCommand,
  StopeNoteDto,
  StopeNoteRouteImageDto,
  UnapproveStopeNoteApproverCommand
} from './api';
import {
  makeGetQuery,
  makeListQuery,
  makeUpdateMutation
} from './useApi';
import { POURS_KEY } from './usePours';
import { MINE_MODEL_KEY, MINE_MODEL_SUMMARY_KEY, RETICULATION_DATA_EXPORT_KEY } from './useMineModels';
import { RECIPES_KEY } from './useRecipes';

export const STOPE_NOTES_KEY = 'stope-notes';
export const STOPE_NOTE_ROUTE_IMAGES_KEY = 'stope-note-route-images';

export interface StopeNotesQueryParams {
  mineModelId: string;
  stopeId: string;
  scenarioId?: string;
}

// Stope notes generated for a stope + scenario (latest carries the downloadable file).
export const useStopeNotesQuery = makeListQuery<StopeNotesQueryParams, StopeNoteDto[]>(
  STOPE_NOTES_KEY,
  (api, params) => () => api.stopeNotes.getStopeNotes(params.mineModelId, params.stopeId, params.scenarioId),
);

export interface DownloadStopeNoteParams {
  mineModelId: string;
  stopeId: string;
  stopeNoteId: string;
}

export const useDownloadStopeNote = makeGetQuery<FileResponse, DownloadStopeNoteParams>(
  STOPE_NOTES_KEY,
  (api, params) => () => api.stopeNotes.downloadStopeNote(params.mineModelId, params.stopeId, params.stopeNoteId),
);

// Carries the ids on the payload because makeUpdateMutation only threads a single entityId +
// the active scenario; the stope-note endpoints need mineModelId + stopeId + scenarioId.
export interface CreateStopeNoteMutation {
  mineModelId: string;
  stopeId: string;
  isPdf: boolean;
  includeRoutePoints: boolean;
  // The generating user's "Add the rise rate chart" tick. Persisted on the note, so approval prints
  // the same document even when the approver's page never captured a chart.
  includeRiseRate: boolean;
  routePoints: string;
  hglImages: string;
  // Data uri of the browser-rendered rise-rate chart; empty when the stope geometry has not loaded or
  // no pour carries a recipe flow rate, in which case the note omits the section.
  riseRateImageBase64: string;
  // JSON array of the fill slices behind that chart, printed as a table beneath it.
  riseRateSlices: string;
}

export const useCreateStopeNoteMutation = makeUpdateMutation<void, CreateStopeNoteMutation>(
  STOPE_NOTES_KEY,
  STOPE_NOTES_KEY,
  (api, _entityId, scenarioId) => (mutation) => api.stopeNotes.createStopeNote(
    mutation.mineModelId,
    mutation.stopeId,
    scenarioId,
    mutation.isPdf,
    mutation.includeRoutePoints,
    mutation.includeRiseRate,
    mutation.routePoints,
    mutation.hglImages,
    mutation.riseRateImageBase64,
    mutation.riseRateSlices,
  ),
  () => i18n._('A stope note has been generated.'),
  // The backend snapshots the generated note into the stope's documents (via AddDocumentToStope), so
  // invalidate the mine-model query set too — otherwise it only shows on the reports overview after a refresh.
  [STOPE_NOTES_KEY, MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

// Route images attached to a stope note (shared across the stope's pours, per scenario).
export const useStopeNoteRouteImagesQuery = makeListQuery<StopeNotesQueryParams, StopeNoteRouteImageDto[]>(
  STOPE_NOTE_ROUTE_IMAGES_KEY,
  (api, params) => () => api.stopeNotes.getRouteImages(params.mineModelId, params.stopeId, params.scenarioId),
);

export interface AddStopeNoteRouteImagesMutation {
  mineModelId: string;
  stopeId: string;
  images: FileParameter[];
}

export const useAddStopeNoteRouteImagesMutation = makeUpdateMutation<StopeNoteRouteImageDto[], AddStopeNoteRouteImagesMutation>(
  STOPE_NOTE_ROUTE_IMAGES_KEY,
  null,
  (api, _entityId, scenarioId) => (mutation) => api.stopeNotes.addRouteImages(
    mutation.mineModelId,
    mutation.stopeId,
    scenarioId,
    mutation.images,
  ),
  () => i18n._('Route images added to the stope note.'),
  [STOPE_NOTE_ROUTE_IMAGES_KEY],
);

export interface DeleteStopeNoteRouteImageMutation {
  mineModelId: string;
  stopeId: string;
  fileId: string;
}

export const useDeleteStopeNoteRouteImageMutation = makeUpdateMutation<void, DeleteStopeNoteRouteImageMutation>(
  STOPE_NOTE_ROUTE_IMAGES_KEY,
  null,
  (api, _entityId, scenarioId) => (mutation) => api.stopeNotes.deleteRouteImage(
    mutation.mineModelId,
    mutation.stopeId,
    mutation.fileId,
    scenarioId,
  ),
  () => i18n._('Route image removed.'),
  [STOPE_NOTE_ROUTE_IMAGES_KEY],
);

// --- Approval workflow (mirrors the per-pour pour-note approval hooks) ---

// Ids ride on the payload because the stope-note endpoints are keyed by
// mineModelId + stopeId + stopeNoteId rather than a single entity id.
interface StopeNoteIds {
  mineModelId: string;
  stopeId: string;
  stopeNoteId: string;
}

export interface AddStopeNoteApproverMutation extends StopeNoteIds {
  addApprover: AddStopeNoteApproverCommand;
}

export const useAddStopeNoteApproverMutation = makeUpdateMutation<void, AddStopeNoteApproverMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.addApprover(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId, mutation.addApprover),
  (mutation) => `${mutation.addApprover.email} `.concat(i18n._('has been added to the approver list.')),
  [STOPE_NOTES_KEY],
);

export interface RemoveStopeNoteApproverMutation extends StopeNoteIds {
  removeApprover: RemoveStopeNoteApproverCommand;
}

export const useRemoveStopeNoteApproverMutation = makeUpdateMutation<void, RemoveStopeNoteApproverMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.removeApprover(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId, mutation.removeApprover),
  (mutation) => `${mutation.removeApprover.email} `.concat(i18n._('has been removed from the approver list.')),
  [STOPE_NOTES_KEY],
);

export const useRequestStopeNoteApprovalMutation = makeUpdateMutation<void, StopeNoteIds>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.requestApproval(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId),
  () => i18n._('Stope note sent for approval.'),
  [STOPE_NOTES_KEY],
);

export interface ApproverApproveStopeNoteMutation extends StopeNoteIds {
  email: string;
  signature?: FileParameter;
}

export const useApproverApproveStopeNoteMutation = makeUpdateMutation<void, ApproverApproveStopeNoteMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.approveApprover(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId, mutation.email, mutation.signature, undefined),
  (mutation) => i18n._('The stope note was successfully approved for').concat(` ${mutation.email}.`),
  [STOPE_NOTES_KEY],
);

export interface ApproverUnapproveStopeNoteMutation extends StopeNoteIds {
  command: UnapproveStopeNoteApproverCommand;
}

export const useApproverUnapproveStopeNoteMutation = makeUpdateMutation<void, ApproverUnapproveStopeNoteMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.unapproveApprover(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId, mutation.command),
  (mutation) => i18n._('The stope note was successfully un-approved for').concat(` ${mutation.command.email}.`),
  [STOPE_NOTES_KEY],
);

export interface ResendStopeNoteApprovalMutation extends StopeNoteIds {
  form: ResendStopeNoteApprovalCommand;
}

export const useResendStopeNoteApprovalMutation = makeUpdateMutation<void, ResendStopeNoteApprovalMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.resendApproval(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId, mutation.form),
  () => i18n._('Emails for stope note approvals successfully resent.'),
  [STOPE_NOTES_KEY],
);

export interface ApproveStopeNoteMutation extends StopeNoteIds {
  isPdf: boolean;
  includeRoutePoints: boolean;
  routePoints: string;
  hglImages: string;
  riseRateImageBase64: string;
  riseRateSlices: string;
}

export const useApproveStopeNoteMutation = makeUpdateMutation<void, ApproveStopeNoteMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.approveStopeNote(
    mutation.mineModelId,
    mutation.stopeId,
    mutation.stopeNoteId,
    mutation.isPdf,
    mutation.includeRoutePoints,
    mutation.routePoints,
    mutation.hglImages,
    mutation.riseRateImageBase64,
    mutation.riseRateSlices,
  ),
  () => i18n._('The stope note has been approved.'),
  // Approving a stope note advances its pours to Approved and re-snapshots the finalized note into the
  // stope's documents on the backend, so refresh the pours and the mine-model query set too.
  [STOPE_NOTES_KEY, POURS_KEY, MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

// Custom-template variants: the browser renders the project's designated Stope Note template and uploads
// the PDF as CustomPdf; the backend stores those bytes instead of generating the native note.
export interface CreateStopeNoteWithCustomPdfMutation extends CreateStopeNoteMutation {
  customPdf: Blob;
}

export const useCreateStopeNoteWithCustomPdfMutation = makeUpdateMutation<void, CreateStopeNoteWithCustomPdfMutation>(
  STOPE_NOTES_KEY,
  STOPE_NOTES_KEY,
  (api, _entityId, scenarioId) => (mutation) => api.stopeNotes.createStopeNote(
    mutation.mineModelId,
    mutation.stopeId,
    scenarioId,
    mutation.isPdf,
    mutation.includeRoutePoints,
    mutation.includeRiseRate,
    mutation.routePoints,
    mutation.hglImages,
    mutation.riseRateImageBase64,
    mutation.riseRateSlices,
    { data: mutation.customPdf, fileName: 'stope-note.pdf' },
  ),
  () => i18n._('A stope note has been generated.'),
  [STOPE_NOTES_KEY, MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

export interface ApproveStopeNoteWithCustomPdfMutation extends ApproveStopeNoteMutation {
  customPdf: Blob;
}

export const useApproveStopeNoteWithCustomPdfMutation = makeUpdateMutation<void, ApproveStopeNoteWithCustomPdfMutation>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.approveStopeNote(
    mutation.mineModelId,
    mutation.stopeId,
    mutation.stopeNoteId,
    mutation.isPdf,
    mutation.includeRoutePoints,
    mutation.routePoints,
    mutation.hglImages,
    mutation.riseRateImageBase64,
    mutation.riseRateSlices,
    { data: mutation.customPdf, fileName: 'stope-note.pdf' },
  ),
  () => i18n._('The stope note has been approved.'),
  [STOPE_NOTES_KEY, POURS_KEY, MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

export const useUnapproveStopeNoteMutation = makeUpdateMutation<void, StopeNoteIds>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.unapproveStopeNote(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId),
  () => i18n._('The stope note has been unapproved.'),
  [STOPE_NOTES_KEY],
);

export const useVoidStopeNoteMutation = makeUpdateMutation<void, StopeNoteIds>(
  STOPE_NOTES_KEY,
  null,
  (api) => (mutation) => api.stopeNotes.voidStopeNote(mutation.mineModelId, mutation.stopeId, mutation.stopeNoteId),
  () => i18n._('The stope note has been voided.'),
  // Voiding clears the note's snapshot from the stope's documents on the backend, so refresh the
  // mine-model query set too — the mirror of what generating one invalidates.
  [STOPE_NOTES_KEY, MINE_MODEL_KEY, RETICULATION_DATA_EXPORT_KEY, RECIPES_KEY, MINE_MODEL_SUMMARY_KEY],
);

// --- Public (emailed token link) approver flow ---

export const PUBLIC_STOPE_NOTE_KEY = 'public-stope-note';
export const PUBLIC_STOPE_NOTE_FILE_KEY = 'public-stope-note-file';

export interface PublicStopeNoteParams {
  mineModelId: string;
  stopeNoteId: string;
  token: string;
}

export const useGetPublicStopeNote = makeGetQuery<PublicStopeNoteApprovalViewDto, PublicStopeNoteParams>(
  PUBLIC_STOPE_NOTE_KEY,
  (api, params) => () => api.publicStopeNotes.get(params.mineModelId, params.stopeNoteId, params.token),
);

export const usePublicDownloadStopeNote = makeGetQuery<FileResponse, PublicStopeNoteParams>(
  PUBLIC_STOPE_NOTE_FILE_KEY,
  (api, params) => () => api.publicStopeNotes.getStopeNoteFile(params.mineModelId, params.stopeNoteId, params.token),
);

export interface PublicStopeNoteApproveMutation {
  mineModelId: string;
  stopeNoteId: string;
  accessTokenHash: string;
  comment?: string;
  signature?: FileParameter;
}

// email is resolved from the token server-side, so the form email arg is left undefined.
export const usePublicApproveStopeNote = makeUpdateMutation<void, PublicStopeNoteApproveMutation>(
  PUBLIC_STOPE_NOTE_KEY,
  null,
  (api) => (mutation) => api.publicStopeNotes.publicApproveApprover(
    mutation.mineModelId,
    mutation.stopeNoteId,
    mutation.accessTokenHash,
    undefined,
    mutation.signature,
    mutation.comment,
  ),
  () => i18n._('The stope note has been successfully approved.'),
);

export interface PublicStopeNoteRejectMutation {
  mineModelId: string;
  stopeNoteId: string;
  accessTokenHash: string;
  comment?: string;
}

export const usePublicRejectStopeNote = makeUpdateMutation<void, PublicStopeNoteRejectMutation>(
  PUBLIC_STOPE_NOTE_KEY,
  null,
  (api) => (mutation) => api.publicStopeNotes.publicRejectStopeNote(
    mutation.mineModelId,
    mutation.stopeNoteId,
    mutation.accessTokenHash,
    mutation.comment,
  ),
  () => i18n._('The stope note has been successfully rejected.'),
);
